import type { SessionSummary } from '@league/shared/contract';
import { DIRECTOR_LIFECYCLE_SESSION_DURATION_MS } from '@league/shared/league-lifecycle';
import { LINK_CLASS, StatusChip } from '@league/ui';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { leagueDate, leagueTime } from '../../lib/when';
import { scoreState } from './session-state';

/**
 * THE SESSIONS, AS A ROW EACH.
 *
 * This replaced a list of a number, a date and a status chip. The commissioner's feedback on that
 * list was that it "is not enough information for a director to understand what is happening at a
 * glance" — and she was right: who is coming, who is away, whether anybody is standing in, whether
 * the seating is done and whether the scores went in were each a click away on a different tab.
 *
 * THE NEXT SESSION CARRIES THE DETAIL. A league is eight weeks long at most, and seven of those
 * rows are not what she came to look at — week six in five weeks' time has no news. So the row that
 * is next says everything and the rest stay quiet until they get closer, which is her own request
 * and also the only way the list stays readable at 390px.
 *
 * A ROW SAYS WHAT IS TRUE, NOT WHETHER IT IS GOOD. "No seating yet" on a session five weeks out is
 * a fact; on tomorrow's it is a job. The difference is in what the row says next to it, never in a
 * colour — the roster-lock card was removed from this page for making an unfilled roster feel like
 * a failure, and a list of amber warnings for perfectly normal weeks would be the same mistake with
 * more rows.
 */

type Phase = 'past' | 'now' | 'next' | 'later';

function phaseOf(session: SessionSummary, now: number, nextId: string | null): Phase {
  const start = Date.parse(session.startsAt);
  if (now >= start + DIRECTOR_LIFECYCLE_SESSION_DURATION_MS) return 'past';
  if (now >= start) return 'now';
  return session.id === nextId ? 'next' : 'later';
}

/** "24 playing", and the two things she can still act on. */
function attendance(session: SessionSummary): string[] {
  const facts = [`${session.expected} playing`];
  if (session.absent > 0) facts.push(`${session.absent} away`);
  if (session.subsNeeded > 0) {
    facts.push(`${session.subsNeeded} ${session.subsNeeded === 1 ? 'chair' : 'chairs'} to fill`);
  }
  if (session.subsConfirmed > 0) {
    facts.push(`${session.subsConfirmed} standing in`);
  }
  return facts;
}

function seatingWords(session: SessionSummary): string {
  if (session.seating === 'published') {
    return `Seating published · ${session.tables} ${session.tables === 1 ? 'table' : 'tables'}`;
  }
  if (session.seating === 'saved') return 'Seating drafted, not published';
  return 'No seating yet';
}

/**
 * ⚠ "SCORES IN FOR 22 OF 24" NEEDS BOTH NUMBERS. A bare count cannot be read: twenty-two is
 * everybody at a league of twenty-two and two tables short at a league of twenty-six, and she is
 * reading it on a phone in a car park.
 */
function scoreWords(session: SessionSummary): string {
  if (session.scoresApproved) return 'Scores approved';
  if (session.scoresClosedAt) return 'Scores settled';
  if (session.scoresEntered === 0) return 'No scores yet';
  return `Scores in for ${session.scoresEntered} of ${session.expected}`;
}

/**
 * A ROW THAT IS A LINK, OR A ROW THAT IS NOT, WITHOUT THE CALLER BRANCHING TWICE.
 *
 * The detailed row carries its own two links, so wrapping it would nest anchors — invalid HTML, and
 * both the keyboard and a screen reader make a mess of it. Everything else is one big target, which
 * is also the only version of this that works with a thumb.
 */
function RowShell({
  to,
  className,
  children,
}: {
  to: string | null;
  className: string;
  children: ReactNode;
}) {
  if (!to) return <div className={className}>{children}</div>;
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

export function SessionRows({
  sessions,
  timeZone,
  venueName,
  now = Date.now(),
}: {
  sessions: SessionSummary[];
  timeZone: string;
  venueName: string | null;
  now?: number;
}) {
  const scheduled = sessions
    .filter((session) => session.status !== 'voided')
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));

  /* The soonest one that has not started. There may be none — a finished league is all past. */
  const nextId = scheduled.find((session) => Date.parse(session.startsAt) > now)?.id ?? null;

  /**
   * ⚠ EXACTLY ONE ROW IS THE DETAILED ONE, and it is the session she is in the middle of if there
   * is one, otherwise the next.
   *
   * The first version marked both, which on a Wednesday evening put the accent bar on two rows at
   * once — two signal elements at rest on one screen, against the palette's rule, and it read as a
   * rendering fault rather than as emphasis. "Tonight" and "next week" are also not the same
   * question: while a session is running, that is the one she is looking at.
   */
  const focusId =
    scheduled.find((session) => phaseOf(session, now, nextId) === 'now')?.id ?? nextId;

  if (scheduled.length === 0) {
    return <p className="text-sm text-ink-2">No sessions are scheduled yet.</p>;
  }

  return (
    <ol className="flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
      {scheduled.map((session) => {
        const phase = phaseOf(session, now, nextId);
        const detailed = session.id === focusId;
        const status = scoreState(session, new Date(now));

        /*
          The facts each phase actually answers. A past session is a question about scores; the next
          one is a question about the room; a later one is a question about the diary, and answering
          more than that is noise she has to read past every week.
        */
        const facts = detailed
          ? [...attendance(session), seatingWords(session)]
          : phase === 'past'
            ? [scoreWords(session), ...(session.absent > 0 ? [`${session.absent} away`] : [])]
            : [`${session.expected} playing`];

        /*
          ⚠ EVERY ROW OPENS ITS SESSION — owner, 2026-09-19.

          Only the detailed row had links, on the reasoning that week six in five weeks has no news.
          That is true of the DETAIL and false of the destination: a row reading "Scores in for 15 of
          18" that cannot be clicked to go and fix those three is the most frustrating kind of dead
          end, because it names the job and then refuses to start it.

          Where it goes depends on what the night needs. A session that has been and gone is a
          question about scores; one that has not is a question about the room.

          ⚠ THE DETAILED ROW IS DELIBERATELY NOT WRAPPED. It already offers both destinations
          explicitly, and an anchor inside an anchor is invalid HTML that screen readers and the
          keyboard both handle badly.
        */
        const destination = `/director/sessions/${session.id}/${phase === 'past' ? 'scores' : 'seating'}`;

        return (
          <li
            key={session.id}
            /*
              The next session gets the one signal mark on this list — a left bar, which is an
              element at rest and therefore spends the budget. Nothing else here may take it.
            */
            className={detailed ? 'border-l-2 border-signal' : 'transition-colors hover:bg-sand'}
          >
          <RowShell to={detailed ? null : destination} className={`block px-5 ${detailed ? 'py-4' : 'py-3.5'}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1.5">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="tabular text-[13px] text-ink-3">{session.sessionNumber}</span>
                <span
                  className={
                    detailed
                      ? 'text-[15px] font-medium text-ink'
                      : 'text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3'
                  }
                >
                  {leagueDate(session.startsAt, timeZone, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
                {detailed ? (
                  <span className="text-[13px] text-ink-2">
                    {leagueTime(session.startsAt, timeZone)}
                  </span>
                ) : null}
              </div>
              <StatusChip status={status} />
            </div>

            {/*
              The venue on the detailed row only. It is the same room every week, so repeating it
              eight times says nothing — but on the night that is next, it is the line somebody
              checks before leaving the house.
            */}
            {detailed && venueName ? (
              <p className="mt-1 text-[13px] text-ink-2">{venueName}</p>
            ) : null}

            <p className={`${detailed ? 'mt-2' : 'mt-1'} text-[13px] text-ink-2`}>
              {facts.join(' · ')}
            </p>

            {detailed ? (
              <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1">
                <Link
                  to={`/director/sessions/${session.id}/seating`}
                  className={`text-[13px] ${LINK_CLASS}`}
                >
                  {session.seating === 'none' ? 'Seat this session' : 'Seating'}
                </Link>
                <Link
                  to={`/director/sessions/${session.id}/scores`}
                  className={`text-[13px] ${LINK_CLASS}`}
                >
                  {session.scoresApproved ? 'View scores' : 'Scores'}
                </Link>
              </div>
            ) : null}
          </RowShell>
          </li>
        );
      })}
    </ol>
  );
}
